//frontend/dispatcher-client/src/components/Login.js
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../axiosConfig";
import { refreshSocketAuth } from "../lib/socket";
import { clearStoredAuth } from "../lib/authStorage";
import "../styles/Login.css";

export default function Login({ onLoginSuccess }) {
  const navigate = useNavigate();

  const location = useLocation();
  const redirectAfterLogin = location.state?.from || "/dashboard";

  const [username, setUsername] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("auth.lastEmail")) || "";
    } catch {
      return "";
    }
  });
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [capsOn, setCapsOn] = useState(false);

  const userRef = useRef(null);
  const pwRef = useRef(null);

  useEffect(() => {
    if (!username && userRef.current) userRef.current.focus();
    if (username && pwRef.current) pwRef.current.focus();
  }, []);

  function onKeyEvent(e) {
    const caps = e.getModifierState?.("CapsLock");
    if (typeof caps === "boolean") setCapsOn(caps);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;

    setError("");
    setBusy(true);

    try {
      const { data } = await api.post("/auth/login", { username, password });

      if (!data?.token) throw new Error("No token in response");

      console.log("[login] success, token len=", String(data?.token || "").length);
      console.log("[login] user company_id=", data?.user?.company_id, "role=", data?.user?.role);

      const userJson = JSON.stringify(data.user || {});

      if (keepSignedIn) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", userJson);
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
      } else {
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("user", userJson);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }

      refreshSocketAuth(data.token);
      console.log("[login] refreshSocketAuth issued");

      const name = data?.user?.name || data?.user?.fullName || data?.user?.username || "";
      const email = data?.user?.email || username || "";

      localStorage.setItem("auth_user", JSON.stringify({ name, email }));
      localStorage.setItem("auth.lastEmail", JSON.stringify(email));

      console.log("[login] keepSignedIn=", keepSignedIn);

      onLoginSuccess?.();
      navigate(redirectAfterLogin, { replace: true });
    } catch (err) {
      console.error("[login] failed", err);

      const status = err?.response?.status;
      const serverMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "";

      const isInvalidCredentials =
        status === 401 ||
        /invalid|invalid credentials|wrong password|incorrect|credenciais|palavra-passe/i.test(serverMsg);

      setError(
        isInvalidCredentials
          ? "Palavra-passe errada. Tente novamente."
          : serverMsg || "Falha ao iniciar sessão."
      );

      // IMPORTANTE:
      // Não limpar auth aqui, para evitar flash/reload/remount do formulário.
      // clearStoredAuth();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-bg" aria-hidden="true">
        <div className="login-bg-grid" />
        <div className="login-bg-glow login-bg-glow--a" />
        <div className="login-bg-glow login-bg-glow--b" />
      </div>

      <div className="login-card">
        <header className="login-header">
          <img
            src="/loadevo-logo.png"
            alt="LOADEvo"
            className="login-brand-logo"
          />
          <p className="login-subtitle">Aceda ao seu painel de operações</p>
        </header>

        {error && (
          <div className="login-alert" role="alert">
            <svg viewBox="0 0 24 24" className="login-alert-icon" aria-hidden="true">
              <path
                d="M12 9v4m0 4h.01M10.29 3.86l-8.4 14.54A1.5 1.5 0 003.2 21h17.6a1.5 1.5 0 001.31-2.6L13.7 3.86a1.5 1.5 0 00-2.6 0z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form
          className="login-form"
          onSubmit={handleSubmit}
          onKeyUp={onKeyEvent}
          onKeyDown={onKeyEvent}
        >
          <label className="login-field">
            <span className="login-label">Email ou nome de utilizador</span>
            <div className="login-input-wrap">
              <input
                ref={userRef}
                className="login-input"
                autoComplete="username"
                inputMode="email"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (error) setError("");
                }}
                disabled={busy}
                placeholder="nome@empresa.com"
                required
              />
            </div>
          </label>

          <label className="login-field">
            <span className="login-label">Palavra-passe</span>

            <div className={`login-input-wrap ${error ? "is-invalid" : ""}`}>
              <input
                ref={pwRef}
                className="login-input login-input-pw"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                onKeyUp={onKeyEvent}
                onKeyDown={onKeyEvent}
                disabled={busy}
                placeholder="••••••••"
                required
                aria-invalid={!!error}
              />

              <button
                type="button"
                className="login-eye"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                disabled={busy}
              >
                {showPw ? (
                  <svg viewBox="0 0 24 24">
                    <path
                      d="M17.94 17.94A10.94 10.94 0 0112 20C7 20 2.73 16.11 1 12c.46-1.06 1.08-2.06 1.82-2.94M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-.88M7.11 7.11A10.94 10.94 0 0112 4c5 0 9.27 3.89 11 8-.6 1.4-1.5 2.7-2.6 3.82M3 3l18 18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24">
                    <path
                      d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                )}
              </button>
            </div>

            {capsOn && <div className="login-hint">O Caps Lock está ativado</div>}
          </label>

          <div className="login-row">
            <label className="login-checkbox">
              <input
                type="checkbox"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
                disabled={busy}
              />
              <span>Manter sessão iniciada</span>
            </label>

            <Link className="login-link" to="/forgot-password">
              Esqueceu-se da palavra-passe?
            </Link>
          </div>

          <button type="submit" className="login-btn" disabled={busy}>
            <span className="login-btn-text">
              {busy ? (
                <>
                  <span className="login-spinner" aria-hidden="true" />
                  A iniciar sessão...
                </>
              ) : (
                "Iniciar sessão"
              )}
            </span>
          </button>
        </form>

        <div className="login-footer">
          <a
            className="login-footer-link"
            href="https://loadevo.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Política de Privacidade
          </a>
        </div>
      </div>
    </div>
  );
}